import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Home, BookOpen, X, Network, GraduationCap, User, UserPlus, Eye, FileText, Settings, Baby } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const Sidebar = ({ isOpen, setIsOpen, allConnects = [], allCourses = [], allMembers = [] }) => {
  const { isAdmin, isSuperAdmin, currentUserData, impersonatedUser } = useAuthStore();
  const realIsAdmin = impersonatedUser ? false : isAdmin;
  const location = useLocation();

  // Determinar perfis do usuário
  const userEmail = currentUserData?.email?.toLowerCase() || '';
  const isLeader = currentUserData && (
    allConnects.some(c => c.leaderId === currentUserData.id) ||
    allConnects.some(c => (c.leaderEmail || '').toLowerCase() === userEmail) ||
    !!currentUserData.isLeader
  );
  const isSupervisor = currentUserData && (
    allConnects.some(c => (c.supervisorEmail || '').toLowerCase() === userEmail) ||
    !!currentUserData.isSupervisor
  );
  const isAuxLeader = currentUserData && (
    // Novo: verifica lista de auxiliares (modelo atual)
    allConnects.some(c => Array.isArray(c.auxLeaders) && c.auxLeaders.some(l => l.id === currentUserData.id || (l.email || '').toLowerCase() === userEmail)) ||
    // Legado: mantém suporte aos campos antigos
    allConnects.some(c => c.auxLeaderId === currentUserData.id) ||
    allConnects.some(c => (c.auxLeaderEmail || '').toLowerCase() === userEmail)
  );

  // Helper: verificar se curso foi realizado nos últimos 12 meses (ou está em andamento/futuro)
  const isCourseInLast12Months = (course) => {
    if (!course) return false;
    const now = new Date();
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setFullYear(now.getFullYear() - 1);
    twelveMonthsAgo.setHours(0, 0, 0, 0);

    const parseDate = (val) => {
      if (!val) return null;
      if (val.toDate && typeof val.toDate === 'function') return val.toDate();
      if (val instanceof Date) return val;
      if (typeof val === 'string') {
        const d = new Date(val.includes('T') ? val : `${val}T00:00:00`);
        return !isNaN(d.getTime()) ? d : null;
      }
      const d = new Date(val);
      return !isNaN(d.getTime()) ? d : null;
    };

    const endDate = parseDate(course.endDate);
    if (endDate) return endDate >= twelveMonthsAgo;

    const startDate = parseDate(course.startDate);
    if (startDate) return startDate >= twelveMonthsAgo;

    if (Array.isArray(course.sporadicClasses) && course.sporadicClasses.length > 0) {
      const hasRecentClass = course.sporadicClasses.some(sc => {
        const scDate = parseDate(sc?.date || sc?.startDate);
        return scDate && scDate >= twelveMonthsAgo;
      });
      if (hasRecentClass) return true;
    }

    const createdAt = parseDate(course.createdAt);
    if (createdAt) return createdAt >= twelveMonthsAgo;

    return !course.finalized;
  };

  // Verificar se é professor nos últimos 12 meses (titular ou substituto ativo/recente)
  const isTeacher = currentUserData && allCourses.some(course => {
    if (!isCourseInLast12Months(course)) return false;

    const email = userEmail;
    const userId = currentUserData.id;

    // Verificar se é professor titular
    if ((course.teacherEmail || '').toLowerCase() === email) return true;
    if (userId && course.teacherId === userId) return true;

    // Verificar se é professor substituto ativo/recente
    if (!course.substituteTeacher || !course.substituteTeacher.teacherId) return false;

    const substituteTeacher = allMembers.find(m => m.id === course.substituteTeacher.teacherId);
    const subEmail = (substituteTeacher?.email || '').toLowerCase();
    const isSubUser = (subEmail && subEmail === email) || course.substituteTeacher.teacherId === userId;
    if (!isSubUser) return false;

    const today = new Date();
    const startDate = course.substituteTeacher.startDate ? new Date(course.substituteTeacher.startDate) : null;

    if (course.substituteTeacher.isIndefinite) {
      return !startDate || today >= startDate;
    }

    if (course.substituteTeacher.endDate) {
      const endDate = new Date(course.substituteTeacher.endDate);
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setFullYear(today.getFullYear() - 1);
      twelveMonthsAgo.setHours(0, 0, 0, 0);
      return endDate >= twelveMonthsAgo;
    }

    return true;
  });

  const isCourseAssistant = currentUserData && allCourses.some(course => {
      if (!isCourseInLast12Months(course)) return false;

      const email = userEmail;
      const userId = currentUserData.id;
      
      // Auxiliar da turma inteira
      if (course.auxiliaryTeacherId === userId || course.auxTeacherId === userId) return true;
      if ((course.auxiliaryTeacherEmail || '').toLowerCase() === email || (course.auxTeacherEmail || '').toLowerCase() === email) return true;
      if (Array.isArray(course.auxiliaryTeachers) && course.auxiliaryTeachers.some(a => a.id === userId || (a.email || '').toLowerCase() === email)) return true;

      // Auxiliar de grupo
      if (Array.isArray(course.groups)) {
          return course.groups.some(g => {
              if (g && (g.assistantId === userId || (g.assistantEmail || '').toLowerCase() === email)) return true;
              if (g && Array.isArray(g.assistants) && g.assistants.some(a => a.id === userId || (a.email || '').toLowerCase() === email)) return true;
              return false;
          });
      }
      return false;
  });

  // Determinar se é membro comum (não tem nenhum perfil especial)
  const isCommonMember = !isAdmin && !isLeader && !isSupervisor && !isTeacher && !isAuxLeader && !isCourseAssistant;

  const navItems = [
    // Para membros comuns, mostrar apenas itens básicos
    // Para outros perfis, mostrar todos os itens específicos
    ...(isCommonMember ? [] : [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/' },
      // Membros - Admins veem "Membros"; Líderes/Supervisores veem "Meus Membros"
      ...(isAdmin
        ? [{ id: 'members', label: 'Membros', icon: Users, path: '/membros' }]
        : ((isLeader || isSupervisor || isAuxLeader)
          ? [{ id: 'members', label: 'Meus Membros', icon: Users, path: '/membros' }]
          : []
        )
      ),

      // Connects - Visível para Admins, Líderes, Supervisores e Auxiliares de Connect
      ...((isAdmin || isLeader || isSupervisor || isAuxLeader) ?
        [{ id: 'connects', label: 'Connects', icon: Home, path: '/connects' }] : []),

      // Cursos - Visível para Admins e Professores dos últimos 12 meses
      ...((isAdmin || isTeacher || isCourseAssistant) ?
        [{ id: 'courses', label: 'Cursos', icon: BookOpen, path: '/cursos' }] : []),

      // Meus Alunos - Visível apenas para Professores dos últimos 12 meses
      ...(isTeacher && !isAdmin ? [{ id: 'my-students', label: 'Meus Alunos', icon: GraduationCap, path: '/meus-alunos' }] : []),

      // Hierarquia - Visível para Admins, Líderes e Supervisores
      
      // Hierarquia - Visível para Admins, Líderes e Supervisores
      ...((isAdmin || isLeader || isSupervisor) ? [{ id: 'hierarchy', label: 'Hierarquia', icon: Network, path: '/hierarquia-lideranca' }] : []),

      // Histórico de Relatórios - Visível apenas para Admins
      ...((isAdmin || isSuperAdmin) ? [{ id: 'reports-history', label: 'Relatórios Connect', icon: FileText, path: '/historico-relatorios' }] : []),

      // Impersonate
      ...((realIsAdmin || isSuperAdmin) && !impersonatedUser ? [{ id: 'impersonate', label: 'Visualizar como...', icon: Eye, path: '/visualizar-como' }] : []),
      
      // Configurações Globais
      ...((realIsAdmin || isSuperAdmin) ? [{ id: 'master-admin', label: 'Config. Globais', icon: Settings, path: '/master-admin' }] : []),
    ]),

    // Ministerio Infantil - Visível para todos os usuários logados
    { id: 'kids', label: 'Ministério Infantil', icon: Baby, path: '/kids' },

    // Nova Decisão - Visível para todos os usuários logados
    { id: 'new-decision', label: 'Nova Decisão', icon: UserPlus, path: '/nova-decisao' },
  ].filter(Boolean);

  // Item separado para Minha Área - sempre visível para todos os usuários
  const personalAreaItem = { id: 'personal-portal', label: 'Minha Área', icon: User, path: '/portal-pessoal' };
  const PersonalIcon = personalAreaItem.icon;

  return (
    <>
      {/* Overlay para todas as telas */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#991B1B] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs sm:text-sm">
                {useAuthStore.getState().isSuperAdmin ? "S" : ((currentUserData?.name || currentUserData?.displayName || useAuthStore.getState().tenantData?.name || "U").charAt(0).toUpperCase())}
              </span>
            </div>
            {(() => {
              const { isSuperAdmin, tenantData } = useAuthStore.getState();
              const displayName = currentUserData?.name || currentUserData?.displayName || '';
              const firstName = displayName.trim().split(' ')[0] || (isSuperAdmin ? 'Super Admin' : (tenantData?.name || 'Usuário'));
              return (
                <span className="font-bold text-gray-800 text-sm sm:text-base">{firstName}</span>
              );
            })()}
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 rounded-md hover:bg-gray-100"
          >
            <X size={18} className="sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>

        <nav className="mt-4 sm:mt-6 flex flex-col flex-1">
          {/* Itens principais do menu */}
          <div className="flex-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setIsOpen(false)} // Fecha o sidebar no mobile após selecionar
                  className={`w-full flex items-center space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-gray-100 transition-colors ${isActive ? 'bg-[#991B1B] text-white hover:bg-[#991B1B]' : 'text-gray-700'
                    }`}
                >
                  <Icon size={18} className="sm:w-5 sm:h-5" />
                  <span className="font-medium text-sm sm:text-base">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Separador e item "Minha Área" no final */}
          <div className="border-t border-gray-200 mt-4 pt-4 mb-4">
            <Link
              to={personalAreaItem.path}
              onClick={() => setIsOpen(false)}
              className={`w-full flex items-center space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-gray-100 transition-colors ${location.pathname === personalAreaItem.path ? 'bg-[#991B1B] text-white hover:bg-[#991B1B]' : 'text-gray-700'
                }`}
            >
              <PersonalIcon size={18} className="sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">{personalAreaItem.label}</span>
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;

